import dayjs, { Dayjs } from 'dayjs/esm/index';
import 'dayjs/esm/locale/ru';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';
import isSameOrAfterPlugin from 'dayjs/esm/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';

import {
  BIRTH_CERTIFICATE_RF_PATTERN,
  EMAIL_PATTERN,
  FLAT_PATTERN,
  NAME_PATTERN,
  PHONE_PATTERN,
} from '../constants/REGEXP_PATTERNS';
import {
  DATE_FORMAT,
  givenDaysForPassportUpdate,
  firstPassportExpirationDate,
  isDateInAcceptableRange,
  isDateInvalid,
  passportMinIssueDate,
  secondPassportExpirationDate,
} from '../helpers/dateHelpers';
import {
  CITIZENSHIP,
  DOCUMENT_TYPE,
  PASSPORT_STATUS,
} from '../constants/document';
import { ERROR_DATE } from '../constants/errors';

dayjs.locale('ru');
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfterPlugin);
dayjs.extend(isBetween);

export const notEmptyField: (value) => boolean = (value) => {
  if (value === null) return true;
  return !String(value).trim();
};

export const isPhoneValid: (value) => boolean = (value) =>
  !new RegExp(PHONE_PATTERN.FOR_VALIDATOR, 'gi').test(value);

export const isNameValid: (value) => boolean = (value) =>
  !new RegExp(NAME_PATTERN.FOR_VALIDATOR, 'gi').test(value);

export const isEmailValid: (value: string) => boolean = (value = '') => {
  if (!value) return true;
  return (
    value.length > 100 ||
    !new RegExp(EMAIL_PATTERN.FOR_VALIDATOR, 'gi').test(value)
  );
};

export const isFlatValid: (value) => boolean = (value) =>
  !new RegExp(FLAT_PATTERN.FOR_VALIDATOR, 'gi').test(value);

export const isRussianPassport: (type) => boolean = (type) =>
  type === DOCUMENT_TYPE.PASSPORT_RF;

export const isCitizen: (type) => boolean = (type) =>
  type === CITIZENSHIP.CITIZEN;

export const isResident: (type) => boolean = (type) =>
  type === CITIZENSHIP.FOREIGN_CITIZEN || type === CITIZENSHIP.NON_CITIZEN;

export const validateDocumentNumber: (isRussianPassport, number) => boolean = (
  isRussianPassport,
  number
) => {
  if (!number) return true;
  if (!isRussianPassport) return false;
  return number.replace(/\D/g, '').length !== 10;
};

export const validateDocumentDivisionCode: (
  isRussianPassport,
  divisionCode
) => boolean = (isRussianPassport, divisionCode) =>
  isRussianPassport && divisionCode.replace(/\D/g, '').length !== 6;

export const validateDocumentIssueDate: (
  issueDate,
  birthdate,
  isRussianPassport
) => string | boolean = (issueDate, birthdate, isRussianPassport) => {
  const dayjsDate = dayjs(issueDate, DATE_FORMAT.VISIBLE);
  const passportStatus = getPassportStatus(
    birthdate,
    issueDate,
    isRussianPassport
  );

  const isInValidDate = isDateInAcceptableRange(issueDate);
  const dayjsBirthdate = dayjs(birthdate, DATE_FORMAT.VISIBLE);
  const dayjsBirthdatePlus14Years = dayjsBirthdate.clone().add(14, 'y');
  const isValidBirthdate = dayjsBirthdate.isValid();

  const checkDiffDates =
    isValidBirthdate && !isInValidDate
      ? dayjsDate.isBefore(dayjsBirthdatePlus14Years)
      : false;

  const isPassportTooOld =
    isRussianPassport &&
    !isInValidDate &&
    dayjsDate.isBefore(passportMinIssueDate)
      ? ERROR_DATE.DOCUMENT_TOO_OLD
      : false;

  const isPassportExpired =
    isRussianPassport &&
    !isInValidDate &&
    !isPassportTooOld &&
    passportStatus === PASSPORT_STATUS.EXPIRED
      ? ERROR_DATE.PASSPORT_IS_EXPIRED
      : false;

  const lessThanBirthDate =
    isValidBirthdate && !isInValidDate
      ? dayjsDate.isBefore(dayjsBirthdate) &&
        ERROR_DATE.DOCUMENT_DATE_EARLY_BIRTHDATE
      : false;

  const lessThanAgeForRussianPassport =
    checkDiffDates && isRussianPassport
      ? ERROR_DATE.DOCUMENT_DATE_EARLY_THAN_14
      : false;

  return (
    isInValidDate ||
    isPassportExpired ||
    isPassportTooOld ||
    lessThanBirthDate ||
    lessThanAgeForRussianPassport
  );
};

const personAge = (birthdate: string) => {
  return dayjs().diff(dayjs(birthdate, DATE_FORMAT.VISIBLE), 'year');
};

const firstDayToUpdatePassport = (birthdate: Dayjs, passedYears: number) =>
  birthdate.clone().add(passedYears, 'y');

function lastDayToUpdatePassport(date: Dayjs, passedYears: number) {
  return date
    .clone()
    .add(passedYears, 'y')
    .add(givenDaysForPassportUpdate, 'd');
}

function isDocumentExpired(birthdate, issueDate, passedYears: number) {
  const minIssueDate = firstDayToUpdatePassport(birthdate, passedYears);
  return issueDate.isBefore(minIssueDate) && dayjs().isAfter(minIssueDate);
}

function yearsDiff(birthdate: Dayjs) {
  return personAge(birthdate) >= secondPassportExpirationDate
    ? secondPassportExpirationDate
    : firstPassportExpirationDate;
}

export const getPassportDueDate = (birthdate: string): string => {
  const age = personAge(birthdate);

  return dayjs(birthdate, DATE_FORMAT.VISIBLE)
    .add(age, 'year')
    .add(givenDaysForPassportUpdate, 'day')
    .format(DATE_FORMAT.FOR_TEXT);
};

export const getPassportStatus = (
  birthdate: string,
  issueDate: string,
  isPassportRf: boolean
): string => {
  if (isDateInvalid(issueDate) || isDateInvalid(birthdate) || !isPassportRf)
    return PASSPORT_STATUS.VALID;

  const birthdateDayjs = dayjs(birthdate, DATE_FORMAT.VISIBLE);
  const issueDateDayjs = dayjs(issueDate, DATE_FORMAT.VISIBLE);

  const passedYears = yearsDiff(birthdate);
  const maxIssueDate = lastDayToUpdatePassport(birthdateDayjs, passedYears);
  const today = dayjs().startOf('day');

  const isPassportExpired = isDocumentExpired(
    birthdateDayjs,
    issueDateDayjs,
    passedYears
  );

  if (issueDateDayjs.diff(birthdateDayjs, 'year') < 14)
    return PASSPORT_STATUS.VALID;

  if (issueDateDayjs.isBefore(passportMinIssueDate))
    return PASSPORT_STATUS.VALID;

  if (isPassportExpired && today.isSameOrBefore(maxIssueDate))
    return PASSPORT_STATUS.EXPIRING;
  if (isPassportExpired && today.isAfter(maxIssueDate))
    return PASSPORT_STATUS.EXPIRED;
  return PASSPORT_STATUS.VALID;
};

export const validateBCNumber: (
  isRussianBC: boolean,
  number: string
) => boolean = (isRussianBC, number) => {
  if (!number) return true;
  if (!isRussianBC) return false;
  return !new RegExp(BIRTH_CERTIFICATE_RF_PATTERN.FOR_VALIDATOR, 'gi').test(
    number
  );
};
